import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import { IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { EditDrawerContext } from 'components/@home/@documents/SentDocuments/EditDrawer';
import { SendDrawerContext as SendDocumentFormatDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import FolderDialog from 'components/@home/@documents/SharedDocuments/FolderDialog';
import { SendDrawerContext } from 'components/@home/@documents/SharedDocuments/SendDrawer';
import { ShareDrawerContext } from 'components/@home/@documents/SharedDocuments/ShareDrawer';
import DeleteDialog from 'components/common/DeleteDialog';
import AccountPlusOutlineIcon from 'mdi-react/AccountPlusOutlineIcon';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import EditIcon from 'mdi-react/EditIcon';
import RestoreClockIcon from 'mdi-react/RestoreClockIcon';
import SendIcon from 'mdi-react/SendIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import { bool, func, object, oneOf } from 'prop-types';
import React, { memo, useContext, useRef } from 'react';
import {
  usePauseCloudMirrorMutation,
  useReconnectCloudMirrorMutation,
  useSyncCloudMirrorMutation,
} from 'store/app/entities/CloudMirrorSlice';
import openCloudOAuth from 'utils/cloudOAuth';
import { I18n } from 'utils/i18n';
import useOpenClose from 'utils/useOpenClose';

const FolderCellMenu = ({ folder, isOpen, open, close, actions = {}, section, mirror = null }) => {
  const anchorEl = useRef();
  const [renameIsOpen, renameOpen, renameClose] = useOpenClose();
  const shareDrawer = useContext(ShareDrawerContext);
  const sendDrawer = useContext(SendDrawerContext);
  const sendDocumentFormatDrawer = useContext(SendDocumentFormatDrawerContext);
  const editDrawer = useContext(EditDrawerContext);
  const [isDeleteOpen, openDelete, closeDelete] = useOpenClose();
  const [syncMirror, { isLoading: isSyncing }] = useSyncCloudMirrorMutation();
  const [pauseMirror] = usePauseCloudMirrorMutation();
  const [reconnectMirror] = useReconnectCloudMirrorMutation();

  const handleSyncNow = () => {
    if (mirror?._id) syncMirror(mirror._id);
    close();
  };
  const handlePause = () => {
    if (mirror?._id) pauseMirror({ id: mirror._id, paused: !mirror.paused });
    close();
  };
  const handleOpenInProvider = () => {
    if (mirror?.provider === 'google' && mirror?.externalFolderId) {
      window.open(
        `https://drive.google.com/drive/folders/${mirror.externalFolderId}`,
        '_blank',
        'noopener',
      );
    }
    close();
  };
  const handleReconnect = async () => {
    close();
    if (!mirror?.provider) return;
    try {
      const tokenId = await openCloudOAuth(mirror.provider);
      await reconnectMirror({ tokenId });
    } catch {
      // popup blocked / cancelled / provider error — folder stays needs_reauth
    }
  };
  const needsReauth = mirror?.syncState === 'needs_reauth';

  const stopPropagation = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleRename = () => {
    renameOpen();
    close();
  };
  const handleDelete = () => {
    close();
    openDelete();
  };
  const handleConfirmDelete = () => {
    actions.destroy(folder._id);
    closeDelete();
  };
  const handleShareSubmit = ({ permissions, teamsRemoved } = {}) => {
    if (!permissions) {
      return;
    }
    actions.edit({ id: folder._id, body: { permissions, teamsRemoved } });
  };
  const handleShare = () => {
    shareDrawer.open(folder, handleShareSubmit);
    close();
  };
  const handleSendSubmit = ({ file, sendTo, text }) => {
    // Inbox and chat rows are attachments. Shared documents are SharedFiles.
    if (section === 'home' || section === 'chats') {
      actions.sendMine({ id: file._id, sendTo, text });
    } else {
      actions.send({ id: file._id, sendTo, text });
    }
  };
  const handleDotsClick = e => {
    stopPropagation(e);
    open();
  };
  const handleRenameSubmit = name => {
    renameClose();
    actions.edit({ id: folder._id, body: { name } });
  };
  const handleRestore = () => {
    actions.restore(folder._id);
    close();
  };
  const handleSend = () => {
    if (folder.kind === 'DocumentFormat') {
      sendDocumentFormatDrawer.open(folder, handleSendSubmit);
    } else {
      sendDrawer.open(folder, handleSendSubmit);
    }
    close();
  };
  const handleEdit = () => {
    editDrawer.open(folder);
    close();
  };
  const isReadOnly = !!folder.source?.readOnly;
  // Mirror roots (readOnly but no parent) can be deleted to remove the whole mirror.
  const isMirrorRoot = isReadOnly && !folder.parent;
  const menuOptions =
    folder.deleted && folder.isAdmin
      ? [
          <MenuItem key="restore" onClick={handleRestore}>
            <ListItemIcon>
              <RestoreClockIcon />
            </ListItemIcon>
            <Typography variant="inherit">{I18n.t('FolderCellMenu.Restore')}</Typography>
          </MenuItem>,
          <MenuItem key="delete-forever" onClick={handleDelete}>
            <ListItemIcon>
              <TrashCanOutlineIcon />
            </ListItemIcon>
            <Typography variant="inherit">{I18n.t('FolderCellMenu.Delete forever')}</Typography>
          </MenuItem>,
        ]
      : [
          ...(folder.kind === 'Document' || (folder.kind === 'DocumentFormat' && folder.isAdmin)
            ? [
                <MenuItem key="send" onClick={handleSend}>
                  <ListItemIcon>
                    <SendIcon />
                  </ListItemIcon>
                  <Typography variant="inherit">{I18n.t('FolderCellMenu.Send')}</Typography>
                </MenuItem>,
              ]
            : []),
          ...(isMirrorRoot && folder.isAdmin && needsReauth
            ? [
                <MenuItem key="reconnect" onClick={handleReconnect}>
                  <ListItemIcon>
                    <SyncIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <Typography variant="inherit" color="error">
                    {I18n.t('FolderCellMenu.Reconnect')}
                  </Typography>
                </MenuItem>,
              ]
            : []),
          ...(isMirrorRoot && folder.isAdmin
            ? [
                <MenuItem key="sync-now" onClick={handleSyncNow} disabled={isSyncing}>
                  <ListItemIcon>
                    <SyncIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">{I18n.t('FolderCellMenu.SyncNow')}</Typography>
                </MenuItem>,
                <MenuItem key="pause" onClick={handlePause} disabled={!mirror?._id}>
                  <ListItemIcon>
                    {mirror?.paused ? (
                      <PlayArrowIcon fontSize="small" />
                    ) : (
                      <PauseIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <Typography variant="inherit">
                    {mirror?.paused
                      ? I18n.t('FolderCellMenu.Resume')
                      : I18n.t('FolderCellMenu.Pause')}
                  </Typography>
                </MenuItem>,
                <Tooltip
                  key="open-in-provider"
                  title={mirror?.provider !== 'google' ? 'Coming soon for OneDrive' : ''}
                  placement="right"
                >
                  <span>
                    <MenuItem
                      onClick={handleOpenInProvider}
                      disabled={!(mirror?.provider === 'google' && mirror?.externalFolderId)}
                      sx={{ width: '100%' }}
                    >
                      <ListItemIcon>
                        <OpenInNewIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">
                        {I18n.t('FolderCellMenu.OpenInProvider', {
                          provider: mirror?.provider === 'google' ? 'Google Drive' : 'OneDrive',
                        })}
                      </Typography>
                    </MenuItem>
                  </span>
                </Tooltip>,
                <MenuItem key="mirror-settings" disabled>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">
                    {I18n.t('FolderCellMenu.MirrorSettings')}
                  </Typography>
                </MenuItem>,
                <MenuItem key="stop-mirroring" onClick={handleDelete}>
                  <ListItemIcon>
                    <TrashCanOutlineIcon />
                  </ListItemIcon>
                  <Typography variant="inherit">
                    {I18n.t('FolderCellMenu.StopMirroring')}
                  </Typography>
                </MenuItem>,
              ]
            : []),
          ...(!isReadOnly && folder.isAdmin
            ? [
                <MenuItem
                  key="edit"
                  onClick={folder.kind === 'DocumentFormat' ? handleEdit : handleRename}
                >
                  <ListItemIcon>
                    {folder.kind === 'DocumentFormat' ? <EditIcon /> : <DriveFileRenameOutline />}
                  </ListItemIcon>
                  <Typography variant="inherit">
                    {I18n.t(
                      `FolderCellMenu.${folder.kind === 'DocumentFormat' ? 'Edit' : 'Rename'}`,
                    )}
                  </Typography>
                </MenuItem>,
                <MenuItem key="share" onClick={handleShare}>
                  <ListItemIcon>
                    <AccountPlusOutlineIcon />
                  </ListItemIcon>
                  <Typography variant="inherit">{I18n.t('FolderCellMenu.Share')}</Typography>
                </MenuItem>,
                <MenuItem key="delete" onClick={handleDelete}>
                  <ListItemIcon>
                    <TrashCanOutlineIcon />
                  </ListItemIcon>
                  <Typography variant="inherit">{I18n.t('FolderCellMenu.Delete')}</Typography>
                </MenuItem>,
              ]
            : []),
        ];

  if (menuOptions.length === 0) {
    return null;
  }
  return (
    <div onClick={stopPropagation}>
      <IconButton ref={anchorEl} edge="end" aria-label="comments" onClick={handleDotsClick}>
        <DotsVerticalIcon />
      </IconButton>
      <Menu open={!!isOpen} onClose={close} anchorEl={anchorEl.current}>
        {menuOptions}
      </Menu>
      <DeleteDialog
        message={I18n.t(
          `FolderCellMenu.${folder.deleted ? 'ConfirmDeleteForever' : 'ConfirmDelete'}`,
          {
            name: folder?.name,
          },
        )}
        confirmText={I18n.t(`FolderCellMenu.${folder.deleted ? 'Delete forever' : 'Delete'}`)}
        isOpen={isDeleteOpen}
        onConfirm={handleConfirmDelete}
        onClose={closeDelete}
      >
        <Typography>
          {I18n.t(`FolderCellMenu.${folder.deleted ? 'WillBeDeletedForever' : 'WillBeDeleted'}`, {
            name: folder?.name,
          })}
        </Typography>
      </DeleteDialog>
      <FolderDialog
        folder={folder}
        onSubmit={handleRenameSubmit}
        onClose={renameClose}
        open={renameIsOpen}
      />
    </div>
  );
};

FolderCellMenu.propTypes = {
  actions: object,
  close: func.isRequired,
  folder: object.isRequired,
  isOpen: bool.isRequired,
  mirror: object,
  open: func.isRequired,
  section: oneOf(['chats', 'home', 'sent', 'shared']).isRequired,
};

const areEqual = (prevProps, nextProps) =>
  prevProps.folder._id === nextProps.folder._id &&
  prevProps.isOpen === nextProps.isOpen &&
  prevProps.folder.isAdmin === nextProps.folder.isAdmin &&
  prevProps.mirror?._id === nextProps.mirror?._id &&
  prevProps.mirror?.syncState === nextProps.mirror?.syncState &&
  prevProps.mirror?.paused === nextProps.mirror?.paused;

export default memo(FolderCellMenu, areEqual);
